import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';
import { Icon } from '../components/icons';
import { CARD_FRAME, buttonClasses } from '../components/ui';
import { useCurrentWorkspace } from '../features/billing/plan';
import type { CurrentWorkspace } from '../lib/api';
import { USAGE_PATH } from './nav';

/**
 * "Espace suspendu" (D-183): while the workspace is suspended, every page but
 * Mon offre is replaced by this screen — the server refuses their reads
 * anyway (403). The navigation stays; the owner and admins are sent to Mon
 * offre, an agent to the owner.
 */

/** Mon offre and its anchors stay open: asking for a plan is how a lapsed trial gets out. */
const isOpenWhileSuspended = (pathname: string) => pathname === USAGE_PATH || pathname.startsWith(`${USAGE_PATH}/`);

export function SuspendedScreen({ workspace }: { workspace: CurrentWorkspace }) {
  const { t } = useTranslation();
  const reason = workspace.suspensionReason;
  const title =
    reason === 'trial_expired' ? 'suspended.trialTitle' : reason === 'complimentary_expired' ? 'suspended.complimentaryTitle' : 'suspended.otherTitle';
  const isAgent = workspace.role === 'agent';
  return (
    <section aria-labelledby="suspended-title" className={`${CARD_FRAME} mx-auto flex max-w-[560px] flex-col items-start gap-3 px-6 py-7`}>
      <span aria-hidden className="flex size-11 items-center justify-center rounded-ds-lg bg-warning-50 text-warning-700">
        <Icon name="pause" className="size-5" />
      </span>
      <h1 id="suspended-title" className="font-display text-(length:--text-display) leading-(--text-display--line-height) tracking-(--text-display--letter-spacing)">
        {t(title)}
      </h1>
      <p className="text-ds-base text-ink-2">
        {t('suspended.body')} {t(isAgent ? 'suspended.agentBody' : 'suspended.ownerBody')}
      </p>
      {isAgent ? null : (
        <Link to={USAGE_PATH} className={`touch-target mt-1 ${buttonClasses('primary')}`}>
          {t('suspended.choose')}
        </Link>
      )}
    </section>
  );
}

/**
 * Renders the page, or the suspended screen in its place. Until the workspace
 * is known the page renders: the server stays the authority, and a failed
 * read of the workspace never hides the app.
 */
export function SuspensionGate({ children }: { children: ReactNode }) {
  const { pathname } = useLocation();
  const workspace = useCurrentWorkspace().data;
  if (workspace?.suspendedAt && !isOpenWhileSuspended(pathname)) return <SuspendedScreen workspace={workspace} />;
  return <>{children}</>;
}
